import { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import { IoMdArrowDropright } from "react-icons/io";
import { FaChevronLeft, FaChevronRight, FaRegNewspaper } from "react-icons/fa";
import banner2 from "../assets/banner2.jpg";
import OneNews from "./OneNews";

export default function NewsBanner({ isHomepagePreview }) {
  const appContext = useContext(AppContext);
  const isNewsPage = appContext.navItem === "news";

  const [ready, setReady] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  async function init() {
    setReady(false);
    await appContext.getAllNews();
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  const sortedNews = useMemo(() => {
    const allNews = Array.isArray(appContext.allNews) ? [...appContext.allNews] : [];
    return allNews.sort((a, b) => Number(b?.dateCreated || 0) - Number(a?.dateCreated || 0));
  }, [appContext.allNews]);

  const homepageNews = useMemo(() => sortedNews.slice(0, 4), [sortedNews]);

  // Slideshow Logic
  useEffect(() => {
    if (!isHomepagePreview || homepageNews.length <= 1) return;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % homepageNews.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isHomepagePreview, homepageNews.length]);

  const goToPrevSlide = () => setActiveSlide((prev) => (prev - 1 + homepageNews.length) % homepageNews.length);
  const goToNextSlide = () => setActiveSlide((prev) => (prev + 1) % homepageNews.length);

  if (!ready) {
    return (
      <div className="container py-5 text-center">
        {getInlineLoader()}
      </div>
    );
  }

  // --- VERSION 1: HOMEPAGE SIDEBAR PREVIEW ---
  if (isHomepagePreview) {
    return (
      <div  
        className="card border-0 shadow-sm overflow-hidden mt-5" 
        style={{ borderRadius: "20px", minHeight: "620px", background: "#fff", paddingTop: "var(--topBarHeight)" }}
      >
        <div className="card-header bg-white border-0 p-4 mt-5">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="fw-bold mb-1" style={{ color: "var(--primaryColor)", letterSpacing: "1px" }}>
                UPDATES
              </h6>
            </div>
            <div className="d-flex gap-2">
              <button onClick={goToPrevSlide} className="btn-nav-circle"><FaChevronLeft size={12} /></button>
              <button onClick={goToNextSlide} className="btn-nav-circle"><FaChevronRight size={12} /></button>
            </div>
          </div>
        </div>

        <div className="card-body p-4">
          <div className="position-relative overflow-hidden" style={{ height: "420px" }}>
            {homepageNews.length > 0 ? (
              <div 
                className="transition-slide" 
                key={activeSlide} // Triggers animation on change
              >
                <OneNews data={homepageNews[activeSlide]} showDescription={false} />
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                <FaRegNewspaper size={40} className="mb-2 opacity-25" />
                <p>No recent updates found.</p>
              </div>
            )}
          </div>
        </div>

        <div className="card-footer bg-white border-0 p-4 pt-0">
          <button
            onClick={() => appContext.navTo({ item: "news" })}
            className="btn w-100 fw-bold py-3 text-white"
            style={{ 
                backgroundColor: "var(--primaryColor)", 
                borderRadius: "12px",
                fontSize: "14px" 
            }}
          >
            Explore All News
          </button>
        </div>

        <style>{`
          .btn-nav-circle {
            width: 32px; height: 32px; border-radius: 50%; border: 1px solid #eee;
            background: #fff; color: #555; display: flex; align-items: center; justify-content: center;
            transition: all 0.2s;
          }
          .btn-nav-circle:hover { background: var(--primaryColor); color: #fff; border-color: var(--primaryColor); }
          .transition-slide { animation: fadeInRight 0.5s ease-out; }
          @keyframes fadeInRight {
            from { opacity: 0; transform: translateX(20px); }
            to { opacity: 1; transform: translateX(0); }
          }
        `}</style>
      </div>
    );
  }

  // --- VERSION 2: FULL NEWS PAGE ---
  return (
    <div 
      className="MainBodySection bg-white"
      style={{
        paddingBottom: "75px" // Dynamic padding logic applied here
      }}
    >
    {/* Dynamic Top Padding Logic Applied here */}
    <div className={`container ${isNewsPage ? "pt-4" : "pt-2"}`} >
      <header>
        <div className="container py-5" >
            <h2 className="w-header-title text-secondary text-start" style={{ fontSize: "clamp(2.4rem, 6vw, 4.8rem)", fontWeight: 900, lineHeight: 1, letterSpacing: "-0.06em", color: "#fff", fontFamily: "'Inter', system-ui, sans-serif" }}>
                News & Updates
            </h2>
        </div>
    </header>
    </div>

      <div className="container py-2">

        <div className="row g-4">
          {sortedNews.length > 0 ? (
            sortedNews.map((item, i) => (
              <div key={i} className={isNewsPage ? "col-lg-6" : "col-12"}>
                <div className="h-100 p-2 shadow-hover transition-all" style={{ borderRadius: "16px" }}>
                  <OneNews data={item} />
                </div>
              </div>
            ))
          ) : (
            <div className="col-md-12 mb-1 mt-1">
              <h6>No data was found</h6>
            </div>
          )}
        </div>
      </div> 

      <style>{`
        .shadow-hover:hover {
            transform: translateY(-5px);
            background: #fafafa;
        }
        .transition-all { transition: all 0.3s ease; }
      `}</style>
    </div>
  );
}